import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  searchFilings,
  fetchFilingDetail,
  updateSearchParam,
  clearSearchParams,
  toggleLiverun,
} from "../reducers/fullPatientFrameSlice";
import LabFilingDetails from "./LabFilingDetails";

const FullPathologySearch = () => {
  const dispatch = useDispatch();
  const {
    searchParams,
    searchResults,
    selectedFiling,
    loadingSearch,
    loadingDetail,
    searchError,
  } = useSelector((state) => state.fullPathologySearch);
  const access_token = useSelector((state) => state.authen.accessToken);
  const loggedInAdmin = useSelector((state) => state.authen.loggedInAdmin);

  const organizationOptions = useMemo(() => {
    if (!loggedInAdmin || !loggedInAdmin.data) return [];

    return [...loggedInAdmin.data]
      .sort((a, b) =>
        (a.organization_name || "").localeCompare(b.organization_name || ""),
      )
      .map((item) => ({
        label: item.organization_name,
        email: item.email,
      }));
  }, [loggedInAdmin]);

  useEffect(() => {
    if (searchResults.length > 0 && access_token) {
      dispatch(searchFilings({ searchParams, access_token }));
    }
  }, [searchParams.liverun, access_token]);

  const [openDialog, setOpenDialog] = useState(false);

  const columns = [
    {
      field: "nhs_number",
      headerName: "NHS Number",
      flex: 1.5,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "emis_id",
      headerName: "EMIS ID",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "license_email",
      headerName: "License Email",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "lab_report_id",
      headerName: "Lab Report Id",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "category",
      headerName: "Category",
      flex: 2,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "category_type",
      headerName: "Report Type",
      flex: 2,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value ? "Normal Report" : "Abnormal Report",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "starttime",
      headerName: "Start Time",
      flex: 1.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "";

        const date = new Date(params.value);

        return date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 4,
      align: "center",
      headerAlign: "center",
    },
  ];

  const handleInputChange = (field, value) => {
    dispatch(updateSearchParam({ field, value }));
  };

  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = () => {
    if (access_token) {
      setHasSearched(true);
      dispatch(searchFilings({ searchParams, access_token }));
    }
  };

  const handleClearSearch = () => {
    setHasSearched(false);
    dispatch(clearSearchParams());
  };

  const handleRowClick = (params) => {
    const lab_report_id = params.row?.lab_report_id;
    const license_email = params.row?.license_email;

    const liverun = searchParams.liverun;

    if (lab_report_id && access_token) {
      dispatch(
        fetchFilingDetail({
          license_email,
          lab_report_id,
          liverun,
          access_token,
        }),
      );
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ mb: 3, fontWeight: "bold", color: "primary.main" }}
        >
          Full Patient Summary
        </Typography>

        {/* Row 1 */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <Autocomplete
            size="small"
            options={organizationOptions}
            getOptionLabel={(option) => option.label || ""}
            filterOptions={(options, state) => {
              const input = (state.inputValue || "").toLowerCase();

              return options.filter((option) =>
                (option.label || "").toLowerCase().includes(input),
              );
            }}
            value={
              organizationOptions.find(
                (org) => org.email === searchParams.license_email,
              ) || null
            }
            onChange={(event, newValue) => {
              handleInputChange(
                "license_email",
                newValue ? newValue.email : "",
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Search Organization" />
            )}
            sx={{ minWidth: 300 }}
          />

          <Box>
            <TextField
              label="NHS Number"
              value={searchParams.nhs_number}
              onChange={(e) =>
                handleInputChange(
                  "nhs_number",
                  e.target.value.replace(/\D/g, ""), // Allow only digits
                )
              }
              size="small"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
            />
          </Box>

          <Box>
            <TextField
              label="Pathology Category"
              value={searchParams.pathology_category}
              onChange={(e) =>
                handleInputChange("pathology_category", e.target.value)
              }
              size="small"
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              label="Start Filing Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={searchParams.start_filing_date || ""}
              onChange={(e) =>
                handleInputChange("start_filing_date", e.target.value)
              }
            />

            <TextField
              label="End Filing Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={searchParams.end_filing_date || ""}
              onChange={(e) =>
                handleInputChange("end_filing_date", e.target.value)
              }
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <FormControlLabel
                sx={{ m: 0, mr: 1 }}
                labelPlacement="start"
                control={
                  <Switch
                    size="small"
                    checked={searchParams.liverun}
                    onChange={() => dispatch(toggleLiverun())}
                    color="success"
                  />
                }
                label={
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: searchParams.liverun ? "#2f855a" : "#c53030",
                    }}
                  >
                    {searchParams.liverun ? "Live Run" : "Dry Run"}
                  </Typography>
                }
              />

              <Button
                variant="outlined"
                startIcon={<X size={18} />}
                onClick={handleClearSearch}
              >
                Clear
              </Button>

              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                disabled={loadingSearch}
              >
                {loadingSearch ? "Searching..." : "Search"}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* {searchError && (
          <Box sx={{ mt: 2 }}>
            <Typography color="error" variant="body2">
              Error: {searchError}
            </Typography>
          </Box>
        )} */}
      </Paper>

      {/* Results Section */}
      {(loadingSearch || hasSearched) && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
            Search Results
            {!loadingSearch && ` (${searchResults.length})`}
          </Typography>

          {loadingSearch ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : searchResults.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 4,
              }}
            >
              <Typography color="text.secondary">
                No results found for the given search criteria.
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={searchResults}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              onRowClick={handleRowClick}
              autoHeight
              sx={{
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          )}
        </Paper>
      )}

      {/* Filing Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Filing Details
          <Button
            onClick={handleCloseDialog}
            sx={{ float: "right" }}
            color="error"
            variant="outlined"
          >
            Close
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading filing...</Typography>
            </Box>
          ) : selectedFiling && Object.keys(selectedFiling).length > 0 ? (
            <>
              <LabFilingDetails lab={selectedFiling} />
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FullPathologySearch;
